import axios, { AxiosError, type InternalAxiosRequestConfig } from "axios";
import * as Sentry from "@sentry/react";
import { supabase, signInAnonymously } from "./supabase";
import { ApiError, ERROR_CODES, RateLimitError } from "./errors";
import type { ErrorBody } from "./errors";
import { storeRateLimitState } from "./rateLimitStorage";
import { isFeatureFlagEnabled } from "./delivery";
import { FeatureFlags } from "./delivery_keys";

const SUPABASE_URL = (import.meta.env.VITE_SUPABASE_URL as string | undefined) ?? "";
const BASE_URL = `${SUPABASE_URL.replace(/\/$/, "")}/functions/v1`;

// claim-checking runs on two runtimes: Supabase Edge (default) and Deno Deploy (the fast path).
// SUPABASE_CLAIMS_URL is always available; DENO_CLAIMS_URL is the Deno Deploy app base URL including
// the /claim-checking path prefix (e.g. https://claim-checking-dev.realitycheq.deno.net/claim-checking),
// set via VITE_CLAIMS_API_URL_DENO. Routing between them is decided per request by a Statsig gate
// (resolveClaimsBaseUrl), so it can be flipped or ramped live without a frontend redeploy.
const SUPABASE_CLAIMS_URL = `${BASE_URL}/claim-checking`;
const DENO_CLAIMS_URL = (import.meta.env.VITE_CLAIMS_API_URL_DENO as string | undefined)?.replace(
  /\/$/,
  ""
);

/** Picks the claim-checking base URL per request: Deno Deploy when the gate is on and a Deno URL is
 *  configured, otherwise Supabase Edge. Gate defaults to false (Supabase) until Statsig initialises. */
function resolveClaimsBaseUrl(): string {
  if (DENO_CLAIMS_URL && isFeatureFlagEnabled(FeatureFlags.claimCheckingUseDenoDeploy)) {
    return DENO_CLAIMS_URL;
  }
  return SUPABASE_CLAIMS_URL;
}

/** True when claim-checking traffic is actively routed to Deno Deploy. */
function isDenoDeployActive(): boolean {
  return !!(DENO_CLAIMS_URL && isFeatureFlagEnabled(FeatureFlags.claimCheckingUseDenoDeploy));
}

/** Fire-and-forget warm ping to the public claim-checking /health endpoint. Boots the serving
 *  isolate so the first real verification hits a warm instance (matters on Deno Deploy, which reuses
 *  isolates). Uses plain fetch, not claimsAPIClient, so it creates no anonymous session; routed by
 *  the same gate as claims traffic, so it warms whichever runtime will serve. */
export function warmClaimsApi(): void {
  void fetch(`${resolveClaimsBaseUrl()}/health`, { method: "GET" }).catch(() => {
    // Best-effort warm-up; a failure here is never user-facing.
  });
}

const HEADER_RL_REMAINING = "x-ratelimit-remaining";
const HEADER_RL_LIMIT = "x-ratelimit-limit";
const HEADER_RL_RESET = "x-ratelimit-reset";
const HEADER_RL_ROUTE = "x-ratelimit-route";

/** Only the create-verification endpoint's quota is tracked for the landing-page gate. Must
 *  match the final path segment of POST /claim-checking/claims/:claim_id/verifications — the
 *  X-RateLimit-Route response header is always the request path's last segment. */
const TRACKED_RL_ROUTE = "verifications";

interface RetryableConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

function toUserMessage(status: number | undefined): string {
  if (status === undefined)
    return "Unable to reach our servers. Check your connection and try again.";
  if (status >= 500) return "Our servers are having trouble. Please try again in a moment.";
  if (status === 422 || status === 400)
    return "Something went wrong with your request. Please try again.";
  return "Something went wrong. Please try again.";
}

/** True if `data` matches the ErrorBody envelope `{ error: { code, message } }`. */
function isErrorBody(data: unknown): data is ErrorBody {
  const body = data as { error?: { code?: unknown; message?: unknown } } | undefined;
  return typeof body?.error?.code === "string" && typeof body?.error?.message === "string";
}

/** Builds an ApiError from a response, trusting the server's ErrorBody envelope when present
 *  and falling back to a generic INTERNAL_ERROR (e.g. network failure, or an old-envelope
 *  response briefly served during a rolling deploy). */
function toApiError(status: number | undefined, data: unknown): ApiError {
  if (isErrorBody(data)) {
    return new ApiError(data.error.message, data.error.code, status ?? 0, data.error.details);
  }
  return new ApiError(toUserMessage(status), ERROR_CODES.INTERNAL.ERROR, status ?? 0);
}

interface ApiClientOptions {
  /** Evaluated per-request to override baseUrl — used to route claim-checking between Supabase
   *  and Deno Deploy via a live feature gate. */
  resolveBaseUrl?: () => string;
  /** When true, reads the X-Cold-Boot response header and logs a Sentry warning when Deno Deploy
   *  is active and a request hit a cold isolate (meaning the warm ping didn't prevent it). */
  trackColdBoot?: boolean;
}

/**
 * Creates a pre-configured axios instance for an Edge Function API.
 * `baseUrl` is the full base URL for this client including the function path prefix
 * (e.g. `${BASE_URL}/corpus`, `${SUPABASE_CLAIMS_URL}`). Call sites pass only the
 * resource path relative to that prefix.
 * Handles auth header injection, 401 retry (once), and error normalisation.
 * getToken is called per-request so dynamic tokens (session JWTs) stay fresh.
 */
function createApiClient(
  baseUrl: string,
  getToken: () => Promise<string>,
  { resolveBaseUrl, trackColdBoot = false }: ApiClientOptions = {}
) {
  const client = axios.create({
    baseURL: baseUrl,
    headers: { "Content-Type": "application/json" },
  });

  client.interceptors.request.use(async (config) => {
    if (resolveBaseUrl) config.baseURL = resolveBaseUrl();
    config.headers.Authorization = `Bearer ${await getToken()}`;
    return config;
  });

  client.interceptors.response.use((response) => {
    const route = response.headers[HEADER_RL_ROUTE];
    const remaining = response.headers[HEADER_RL_REMAINING];
    const limit = response.headers[HEADER_RL_LIMIT];
    const reset = response.headers[HEADER_RL_RESET];
    if (
      route === TRACKED_RL_ROUTE &&
      remaining !== undefined &&
      limit !== undefined &&
      reset !== undefined
    ) {
      storeRateLimitState(Number(remaining), Number(limit), Number(reset));
    }

    if (trackColdBoot && isDenoDeployActive() && response.headers["x-cold-boot"] === "true") {
      Sentry.captureMessage("Deno Deploy cold start detected on request", {
        level: "warning",
        tags: { "cold_start.source": "request" },
        extra: { endpoint: response.config?.url },
      });
    }

    return response;
  });

  client.interceptors.response.use(undefined, async (error: AxiosError) => {
    const config = error.config as RetryableConfig | undefined;

    // Error thrown by the request interceptor (token getter failed before any HTTP request).
    // Neither error.request nor error.response are set in this case — distinct from a genuine
    // network failure (error.request set, error.response undefined). Re-throw directly so the
    // original message reaches the caller instead of the misleading "no connection" fallback.
    if (!error.request && !error.response) {
      return Promise.reject(error);
    }

    if (error.response?.status === 429) {
      const body = error.response.data;
      const retryAfter = isErrorBody(body)
        ? (body.error.details?.retry_after_seconds as number | undefined)
        : undefined;
      const minutes = retryAfter ? Math.ceil(retryAfter / 60) : "a while";
      const route = error.response.headers[HEADER_RL_ROUTE];
      const limit = error.response.headers[HEADER_RL_LIMIT];
      const reset = error.response.headers[HEADER_RL_RESET];
      if (route === TRACKED_RL_ROUTE && limit !== undefined && reset !== undefined) {
        storeRateLimitState(0, Number(limit), Number(reset));
      }
      return Promise.reject(
        new RateLimitError(
          `You've reached your limit. Try again in ${minutes} minute(s).`,
          retryAfter
        )
      );
    }

    if (error.response?.status === 401 && config && !config._retry) {
      config._retry = true;
      return client(config);
    }

    if (error.response?.status === 401) {
      return Promise.reject(
        new ApiError(
          "Your session has expired. Please refresh the page.",
          ERROR_CODES.AUTH.UNAUTHENTICATED,
          401
        )
      );
    }

    const status = error.response?.status;
    const body = error.response?.data;
    const responseText = typeof body === "string" ? body : JSON.stringify(body ?? "");
    Sentry.addBreadcrumb({
      category: "api",
      message: `API error — ${error.code ?? "unknown"} ${status ?? "no response"}`,
      data: {
        axiosCode: error.code,
        axiosMessage: error.message,
        method: error.config?.method?.toUpperCase(),
        url: error.config?.url,
        status,
        responseBody: responseText,
      },
      level: "error",
    });

    return Promise.reject(toApiError(status, body));
  });

  return client;
}

/** Public claims API — uses the anonymous or user session JWT from Supabase Auth. */
export const claimsAPIClient = createApiClient(
  SUPABASE_CLAIMS_URL,
  async () => {
    const { data } = await supabase.auth.getSession();
    if (data.session?.access_token) return data.session.access_token;

    // Session missing (e.g. refresh token expired during inactivity) — re-establish anonymously.
    const session = await signInAnonymously();
    if (session?.access_token) return session.access_token;

    throw new Error("Session unavailable. Please refresh the page.");
  },
  { resolveBaseUrl: resolveClaimsBaseUrl, trackColdBoot: true }
);

/** Internal corpus API — requires an active Supabase session with the internal role. */
export const corpusAPIClient = createApiClient(`${BASE_URL}/corpus`, async () => {
  const { data } = await supabase.auth.getSession();
  if (!data.session?.access_token) throw new Error("Not authenticated");
  return data.session.access_token;
});

/** Internal claims API — for internal-only routes on the claim-checking function.
 *  Requires an active Supabase session with the internal role. */
export const internalClaimsAPIClient = createApiClient(
  SUPABASE_CLAIMS_URL,
  async () => {
    const { data } = await supabase.auth.getSession();
    if (!data.session?.access_token) throw new Error("Not authenticated");
    return data.session.access_token;
  },
  { resolveBaseUrl: resolveClaimsBaseUrl, trackColdBoot: true }
);

/** Admin API — platform.admin-gated operations (user management, etc.). */
export const adminAPIClient = createApiClient(`${BASE_URL}/admin`, async () => {
  const { data } = await supabase.auth.getSession();
  if (!data.session?.access_token) throw new Error("Not authenticated");
  return data.session.access_token;
});

/** Expert insights API — expert_insights.expert-gated operations. */
export const expertInsightsAPIClient = createApiClient(`${BASE_URL}/expert-insights`, async () => {
  const { data } = await supabase.auth.getSession();
  if (!data.session?.access_token) throw new Error("Not authenticated");
  return data.session.access_token;
});
